import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resources } from '../../resources/data';
import { industries } from '../../industries/data';
import { useInView } from '../../../hooks/useInView';

/**
 * One anonymous case study per Built For industry. `industry` is the slug used by the Built For menu and
 * /industries, so labels, icons and colours stay in sync with them.
 */
const TABS = [
  { industry: 'restaurants', slug: 'qsr-route-optimization' },
  { industry: 'grocery', slug: 'grocery-qcommerce-rapid-delivery' },
  { industry: 'pharmacies', slug: 'pharmacy-coldchain-compliance' },
  { industry: 'couriers', slug: 'cep-parcel-network-dispatch' },
  { industry: 'contract-logistics', slug: '3pl-multi-client-visibility' },
] as const;

const ADVANCE_MS = 8000;

export default function IndustryImpact() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const { ref: sectionRef, inView } = useInView<HTMLElement>(0.3);
  const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const count = TABS.length;

  useEffect(() => {
    if (!inView || reduceMotion) return;
    const id = window.setTimeout(() => setActive((i) => (i + 1) % count), ADVANCE_MS);
    return () => window.clearTimeout(id);
  }, [active, inView, reduceMotion, count]);

  const studies = resources['case-studies']?.caseStudies ?? [];
  const tabs = TABS.map((tab) => ({ ...tab, page: industries[tab.industry], study: studies.find((s) => s.slug === tab.slug) })).filter((tab) => tab.study && tab.page);
  const cur = tabs[active];
  if (!cur || !cur.study || !cur.page) return null;
  const s = cur.study;
  const color = cur.page.color ?? '#2563eb';

  return (
    <section className="relative overflow-hidden border-t border-gray-100 bg-slate-50/70 py-14 lg:py-16" aria-labelledby="industry-impact-title" ref={sectionRef}>
      <div className="pointer-events-none absolute right-0 top-0 h-96 w-96 translate-x-1/3 -translate-y-1/3 rounded-full bg-blue-400/10 blur-3xl" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mx-auto mb-7 max-w-3xl text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('home.industryImpact.eyebrow')}</p>
          <h2 id="industry-impact-title" className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl">
            {t('home.industryImpact.title')} <span className="block text-blue-600">{t('home.industryImpact.titleHighlight')}</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-gray-600">{t('home.industryImpact.subtitle')}</p>
        </div>

        <div role="tablist" aria-label={t('home.industryImpact.eyebrow')} className="mb-8 flex flex-wrap justify-center gap-2">
          {tabs.map((tab, i) => (
            <button
              key={tab.slug}
              type="button"
              role="tab"
              id={`impact-tab-${tab.industry}`}
              aria-selected={i === active}
              aria-controls="impact-panel"
              onClick={() => setActive(i)}
              className={`relative inline-flex items-center gap-2 overflow-hidden rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
                i === active ? 'border border-transparent text-white shadow-lg' : 'border border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }`}
              style={i === active ? { backgroundColor: tab.page?.color ?? '#2563eb', boxShadow: `0 10px 25px -8px ${tab.page?.color ?? '#2563eb'}` } : undefined}
            >
              <i className={`${tab.page?.icon ?? 'ri-building-line'} text-base`} aria-hidden />
              {t(`nav.items.builtFor.${tab.industry}.label`)}
              {i === active && inView && !reduceMotion && (
                <span
                  key={active}
                  className="absolute bottom-0 left-0 h-0.5 bg-white/70"
                  style={{ animation: `impact-progress ${ADVANCE_MS}ms linear forwards` }}
                  aria-hidden
                />
              )}
            </button>
          ))}
        </div>

        <div id="impact-panel" role="tabpanel" aria-labelledby={`impact-tab-${cur.industry}`} key={cur.slug} className="grid items-start gap-10 lg:grid-cols-12 lg:gap-14 animate-fade-in-up">
          {/* Story */}
          <div className="lg:col-span-5">
            <p className="inline-flex flex-wrap items-center gap-2 text-sm font-semibold" style={{ color }}>
              <i className={`${cur.page.icon} text-base`} aria-hidden />
              {t(`nav.items.builtFor.${cur.industry}.label`)}
              <span className="text-gray-300">·</span>
              <span className="font-medium text-gray-500">{s.company}</span>
            </p>
            <h3 className="mt-4 text-3xl font-bold leading-tight tracking-tight text-gray-900 sm:text-[2.1rem]">{s.headline}</h3>

            <dl className="mt-8 space-y-6">
              <div className="relative border-s-2 border-gray-200 ps-6">
                <span className="absolute -start-[7px] top-1 h-3 w-3 rounded-full bg-orange-500 ring-4 ring-slate-50" aria-hidden />
                <dt className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-900">{t('home.industryImpact.challenge')}</dt>
                <dd className="mt-2 text-[15px] leading-relaxed text-gray-600">{s.challenge}</dd>
              </div>
              <div className="relative border-s-2 border-gray-200 ps-6">
                <span className="absolute -start-[7px] top-1 h-3 w-3 rounded-full bg-emerald-500 ring-4 ring-slate-50" aria-hidden />
                <dt className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-900">{t('home.industryImpact.solution')}</dt>
                <dd className="mt-2 text-[15px] leading-relaxed text-gray-600">{s.solution}</dd>
              </div>
            </dl>

            <div className="mt-8 flex flex-wrap items-center gap-5">
              <Link
                to={`/resources/case-studies/${s.slug}`}
                className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800"
              >
                {t('home.industryImpact.readCaseStudy')}
                <i className="ri-arrow-right-up-line" aria-hidden />
              </Link>
              <Link to={`/industries/${cur.industry}`} className="text-sm font-semibold text-gray-600 hover:text-gray-900">
                {t('home.industryImpact.industryPage')}
              </Link>
            </div>
          </div>

          {/* Visual + proof */}
          <div className="lg:col-span-7">
            <div className="relative lg:pe-10">
              <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl shadow-blue-900/5">
                <img src={s.heroImage} alt={s.headline} loading="lazy" className="aspect-[16/10] w-full object-cover" />
              </div>
              <ul className="m-0 mt-4 grid list-none grid-cols-3 gap-3 p-0 lg:absolute lg:end-0 lg:top-8 lg:mt-0 lg:flex lg:w-44 lg:flex-col">
                {s.metrics.slice(0, 3).map((m) => (
                  <li key={m.label} className="rounded-2xl border border-gray-200 bg-white/95 px-4 py-3 shadow-lg shadow-blue-900/10 backdrop-blur">
                    <p className="text-2xl font-bold leading-none" style={{ color }}>{m.value}</p>
                    <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500">{m.label}</p>
                  </li>
                ))}
              </ul>
            </div>
            {s.quote && (
              <figure className="relative mt-6 rounded-3xl bg-gray-900 p-6 text-white sm:p-8 lg:me-10">
                <i className="ri-double-quotes-l absolute end-6 top-5 text-4xl text-white/10" aria-hidden />
                <blockquote className="m-0 text-base leading-relaxed text-slate-200 sm:text-lg">“{s.quote}”</blockquote>
                <figcaption className="mt-4 text-sm text-slate-400">
                  <span className="font-semibold text-white">{s.author}</span> · {s.company}
                </figcaption>
              </figure>
            )}
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-gray-500">
          <Link to="/resources/case-studies" className="font-semibold text-blue-700 hover:text-blue-600">
            {t('home.industryImpact.allCaseStudies')} <i className="ri-arrow-right-line align-middle rtl:rotate-180" aria-hidden />
          </Link>
        </p>
      </div>
      <style>{`@keyframes impact-progress { from { width: 0 } to { width: 100% } }`}</style>
    </section>
  );
}
